import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollReveal } from '../hooks/useScrollReveal';

const HomePage = () => {
  const navigate = useNavigate();

  // Scroll reveal hooks
  const heroReveal = useScrollReveal({ threshold: 0.2, triggerOnce: true });
  const philosophyReveal = useScrollReveal({ threshold: 0.15, triggerOnce: true });

  useEffect(() => {
    // document.title = "Domigo — Premium Dog Boarding";

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta(
      "description",
      "Domigo — premium dog boarding in [City]. Where dogs don’t count days; they fill them."
    );
    setMeta(
      "keywords",
      "dog boarding in [City], premium dog hostel in [City], pet boarding in [City], dog daycare in [City]"
    );

    return () => { };
  }, []);

  return (
    <div className="min-h-screen flex flex-col visitor-page">
      <main className="flex-1">
        {/* Hero with scroll animation */}
        <section className="bg-gradient-to-br from-white via-domigo-pink/5 to-white py-20">
          <div
            ref={heroReveal.ref}
            className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${heroReveal.isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-12'
              }`}
          >


            <h1 className="home-hero-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent leading-tight tracking-tight mb-2">
              Finally, a Dog Boarding Where Dogs Don’t Wait for You to Come Back.
            </h1>

            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
              Welcome to Domigo — premium dog boarding in Indore, where dogs don’t count days. They fill them. With friends. With play. With purpose.
            </p>

            <div className="max-w-md mx-auto flex gap-4 justify-center">
              <button
                onClick={() => navigate("/contact")}
                className="px-6 py-3 bg-gradient-pink text-white rounded-full font-semibold shadow-lg btn-smooth cta-pulse"
              >
                Contact Us
              </button>

              <button
                onClick={() => navigate("/contact")}
                className="px-6 py-3 border-2 border-gray-200 rounded-full text-gray-800 font-medium hover:bg-gray-50 hover:border-domigo-pink transition-all duration-300 btn-smooth"
              >
                Schedule a Visit
              </button>
            </div>
          </div>
        </section>
        {/* Philosophy with scroll animation */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h4 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4 text-center">Domigo Philosophy</h4>

            <div
              ref={philosophyReveal.ref}
              className={`max-w-5xl mx-auto transition-all duration-1000 ${philosophyReveal.isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12'
                }`}
            >
              <div className="bg-white shadow-md rounded-2xl p-8 sm:p-12 text-gray-700">
                <div className="grid gap-8 md:grid-cols-2 items-start">
                  <div>
                    <p className="text-lg lg:text-xl text-gray-800 font-semibold mb-4">Most dog boarding facilities manage dogs. Domigo fulfils them.</p>

                    <p className="text-base text-gray-600 mb-4">Let’s be honest — dogs don’t dislike boarding because they miss their homes. They dislike it because they lose their rhythm of life.</p>

                    <ul className="list-disc list-inside text-gray-700 space-y-3 mb-4">
                      <li>The movement</li>
                      <li>The friendships</li>
                      <li>The sense of belonging</li>
                    </ul>

                    <p className="text-base text-gray-600 mb-6">Domigo was built to restore that life — not just supervise it. Here, dogs don’t stay in kennels; they thrive in structured, compatible packs.</p>

                    <p className="text-base text-gray-600">They run with purpose, socialise with confidence and rest with the calm satisfaction of a day fully lived.</p>
                  </div>

                  <aside className="space-y-6">
                    <div className="p-4 bg-domigo-pink/5 border border-domigo-pink/10 rounded-xl">
                      <p className="text-xs text-gray-500">Core Belief</p>
                      <p className="text-lg font-bold mt-1 text-gray-900">A tired dog is good. A fulfilled dog is unforgettable.</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                      <p className="text-sm font-semibold text-gray-800 mb-3">Why Domigo</p>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-3"><span className="inline-block mt-1 w-2 h-2 rounded-full bg-domigo-pink" /> Experienced, certified caregivers</li>
                        <li className="flex items-start gap-3"><span className="inline-block mt-1 w-2 h-2 rounded-full bg-domigo-pink" /> Thoughtful group matching and play plans</li>
                        <li className="flex items-start gap-3"><span className="inline-block mt-1 w-2 h-2 rounded-full bg-domigo-pink" /> Clean, safe and comfortable spaces</li>
                      </ul>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button onClick={() => navigate("/contact")} className="flex-1 px-5 py-3 bg-gradient-pink text-white rounded-full font-semibold shadow-lg btn-smooth cta-pulse">Contact Us</button>
                      <button onClick={() => navigate("/contact")} className="flex-1 px-5 py-3 border-2 border-gray-200 rounded-full text-gray-800 hover:bg-gray-50 hover:border-domigo-pink transition-all duration-300 btn-smooth">Schedule a Visit</button>
                    </div>
                  </aside>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
